import React, { useMemo } from 'react';
import {
  Drawer,
  List,
  Badge,
  Typography,
  Avatar,
  Space,
  Button,
  Empty,
  Tag,
  Spin,
  Alert,
  Tooltip,
  Dropdown,
  MenuProps,
} from 'antd';
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  BellOutlined,
  CheckOutlined,
  ReloadOutlined,
  CloseOutlined,
  MoreOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { theme } from 'antd';
import {
  useFinanceNotifications,
  FinanceNotification,
} from '../context/FinanceNotificationContext';
import '../style/partials/notificationPanel.css';

const { Text, Paragraph } = Typography;

interface FinanceNotificationPanelProps {
  visible: boolean;
  onClose: () => void;
}

const FinanceNotificationPanel: React.FC<FinanceNotificationPanelProps> = ({
  visible,
  onClose,
}) => {
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetchNotifications,
  } = useFinanceNotifications();

  const navigate = useNavigate();
  const { theme: currentTheme } = useTheme();
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? '#1f1f1f' : '#ffffff',
      textColor: isDarkMode
        ? 'rgba(255, 255, 255, 0.85)'
        : 'rgba(0, 0, 0, 0.85)',
      textColorSecondary: isDarkMode
        ? 'rgba(255, 255, 255, 0.45)'
        : 'rgba(0, 0, 0, 0.45)',
      borderColor: isDarkMode ? '#303030' : '#f0f0f0',
      unreadBackground: isDarkMode
        ? 'rgba(24, 144, 255, 0.1)'
        : 'rgba(24, 144, 255, 0.05)',
      itemHoverBackground: isDarkMode
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.02)',
    }),
    [isDarkMode]
  );

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14', fontSize: 20 }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 20 }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />;
    }
  };

  const formatTimestamp = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'system':
        return 'blue';
      case 'user':
        return 'cyan';
      case 'security':
        return 'red';
      case 'update':
        return 'green';
      case 'approval':
        return 'purple';
      case 'review':
        return 'orange';
      default:
        return 'default';
    }
  };

  const handleNotificationClick = async (notification: FinanceNotification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
      return;
    }

    navigate('/finance/dashboard');
    onClose();
  };

  const getNotificationMenuItems = (
    notification: FinanceNotification
  ): MenuProps['items'] => [
    ...(notification.read
      ? [
          {
            key: 'unread',
            label: 'Mark as unread',
            icon: <EyeOutlined />,
            onClick: (e: any) => {
              e?.domEvent?.stopPropagation();
              markAsUnread(notification.id);
            },
          },
        ]
      : [
          {
            key: 'read',
            label: 'Mark as read',
            icon: <CheckOutlined />,
            onClick: (e: any) => {
              e?.domEvent?.stopPropagation();
              markAsRead(notification.id);
            },
          },
        ]),
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (e: any) => {
        e?.domEvent?.stopPropagation();
        deleteNotification(notification.id);
      },
    },
  ];

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  }, [notifications]);

  return (
    <Drawer
      title={
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Space align="center">
            <BellOutlined />
            <span>Finance Notifications</span>
            {unreadCount > 0 && (
              <Badge
                count={unreadCount}
                size="small"
                style={{ backgroundColor: '#ff4d4f' }}
              />
            )}
          </Space>
          <Space>
            <Tooltip title="Refresh notifications">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={refetchNotifications}
                loading={isLoading}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Mark all as read">
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
                size="small"
              />
            </Tooltip>
          </Space>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={420}
      className="notification-panel finance-notification-panel"
      styles={{
        body: {
          padding: 0,
          background: themeTokens.background,
        },
        header: {
          borderBottom: `1px solid ${themeTokens.borderColor}`,
          background: themeTokens.background,
        },
      }}
      closeIcon={<CloseOutlined />}
    >
      {error && (
        <Alert
          message="Error loading notifications"
          description={error}
          type="error"
          showIcon
          style={{ margin: '16px' }}
          action={
            <Button size="small" onClick={refetchNotifications}>
              Retry
            </Button>
          }
        />
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Spin size="large" />
        </div>
      )}

      {!isLoading && !error && (
        <>
          {notifications.length > 0 && (
            <div style={{ padding: '16px', borderBottom: `1px solid ${themeTokens.borderColor}` }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary">
                  {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                  {unreadCount > 0 && ` • ${unreadCount} unread`}
                </Text>
                <Button
                  type="link"
                  size="small"
                  onClick={clearAllNotifications}
                  style={{ padding: 0, color: themeTokens.textColorSecondary }}
                >
                  Clear all
                </Button>
              </Space>
            </div>
          )}

          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: themeTokens.textColorSecondary }}>
                  No finance notifications
                </span>
              }
              style={{
                marginTop: '60px',
                color: themeTokens.textColorSecondary,
              }}
            />
          ) : (
            <List
              itemLayout="vertical"
              dataSource={sortedNotifications}
              style={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}
              renderItem={(notification) => (
                <List.Item
                  key={notification.id}
                  style={{
                    padding: '16px',
                    borderBottom: `1px solid ${themeTokens.borderColor}`,
                    backgroundColor: notification.read
                      ? 'transparent'
                      : themeTokens.unreadBackground,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleNotificationClick(notification)}
                  actions={[
                    <Dropdown
                      menu={{ items: getNotificationMenuItems(notification) }}
                      trigger={['click']}
                      key="more"
                    >
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={getNotificationIcon(notification.type)}
                        style={{ backgroundColor: 'transparent' }}
                      />
                    }
                    title={
                      <Text strong={!notification.read} style={{ color: themeTokens.textColor }}>
                        {notification.title}
                      </Text>
                    }
                    description={
                      <>
                        <Paragraph
                          style={{ marginBottom: '8px', color: themeTokens.textColorSecondary }}
                          ellipsis={{ rows: 2 }}
                        >
                          {notification.message}
                        </Paragraph>
                        <Space size="small" wrap>
                          {notification.category && (
                            <Tag color={getCategoryColor(notification.category)}>
                              {notification.category}
                            </Tag>
                          )}
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatTimestamp(notification.timestamp)}
                          </Text>
                          <Button
                            type="link"
                            size="small"
                            icon={<FileTextOutlined />}
                            style={{ padding: 0, height: 'auto' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            {notification.actionText || 'View Details'}
                          </Button>
                        </Space>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default React.memo(FinanceNotificationPanel);
